import { capitalizeAll, getErrorMessage } from '@moderepo/mode-app-components';
import { userAppDataContext } from '@moderepo/mode-data-state-user-app';
import React, { CSSProperties, useCallback, useContext, useMemo } from 'react';
import { CircularProgress } from '@material-ui/core';
import { AlertRecipient, AlertRule, AlertTargetMetric, isEmailAlertRecipient, isEventAlertRecipient, isMetricsHeartbeatAlertCondition,
    isMetricsThresholdAlertCondition, MetricsAlertConditionThreshold } from '@moderepo/mode-internal-apis';
import { TFunction } from 'i18next';
import { getAlertConditionTypeDisplayName, getAlertRecipientTypeDisplayName, getAlertSeverityDisplayName, getAlertTargetByDisplayName,
    getEntityClassDisplayValue } from '.';
import { AlertRuleInfo, AlertRuleInfoProps, TargetMetricFields, ThresholdFields } from '../../components/alerting';
import { useRitzTranslation } from '../../utils/customHooks';
import { useSelectAlertRule, useSelectProject } from '../../utils/dataStateHooks';
import { getDateTimeAndElapsedTime } from '../containerUtils';
import { getTimeSeriesAggregationTypeName } from '../time-series/tsUtils';
import { getEntityDisplayName, useSelectTranslatedEntityClasses } from '..';


/**
 * Get the display string for a recipient. A recipient has a type and additional attributes based on the the type. We need to return some
 * user-friendly string that display all the recipient's info.
 */
const getUserFriendlyRecipientDisplay = (recipient: AlertRecipient, trans: TFunction): String => {
    const recipientType = trans(getAlertRecipientTypeDisplayName(recipient.recipientType)).toUpperCase();

    if (isEmailAlertRecipient(recipient)) {
        return trans('alerting.alert_notification.recipient_display_email', {
            email: recipient.email,
        });
    }

    if (isEventAlertRecipient(recipient)) {
        return trans('alerting.alert_notification.recipient_display_event', {
            event_type: recipient.eventType,
        });
    }
    // TODO Add support for other types

    return recipientType;
};


export interface ReadonlyAlertRuleInfoContainerProps {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;
    readonly projectId: number;
    readonly alertRuleId: number;

    // A flag to tell this container to show the "Edit" button. This container does not have to handle the editing. It will just need
    // to show the button and pass the button event to the parent container.
    readonly canEditAlertRule: boolean;
    readonly onEditAlertRule: ()=> void;
}



/**
 * This container shows the AlertInfo component in readonly view.
 */
export const ReadonlyAlertRuleInfoContainer: React.FC<ReadonlyAlertRuleInfoContainerProps> = (props: ReadonlyAlertRuleInfoContainerProps) => {

    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const entityClasses = useSelectTranslatedEntityClasses(props.projectId, trans);

    // Get the alert rule object for the specified alertRuleId
    const selectedAlertRule = useSelectAlertRule(props.projectId, props.alertRuleId);



    /**
     * Helper function to create the fields that display the attributes for targetMetric
     */
    const getTargetMetricFields = useCallback((targetMetric: AlertTargetMetric): TargetMetricFields => {
        const fields: TargetMetricFields = {
            entityClass: {
                label       : capitalizeAll(trans('field.entity_class.label')),
                displayValue: getEntityClassDisplayValue(targetMetric.entityClass, entityClasses),
                editable    : true,
            },
            metricsDefinitionId: {
                label       : capitalizeAll(trans('field.metrics_definition_id.label')),
                displayValue: targetMetric.metricsDefinitionId,
                editable    : true,
            },
            metricName: {
                label       : capitalizeAll(trans('alerting.label.metric_name')),
                displayValue: targetMetric.metricName ?? '',
                editable    : true,
            },
            targetBy: targetMetric.entityTargetCondition ? {
                label       : capitalizeAll(trans('alerting.label.target_by')),
                displayValue: targetMetric.entityTargetCondition.targetBy
                    ? capitalizeAll(trans(getAlertTargetByDisplayName(targetMetric.entityTargetCondition?.targetBy)))
                    : '',
                editable: true,
            } : undefined,
            entityIds: targetMetric.entityTargetCondition ? {
                label       : capitalizeAll(trans('terms.entity_other')),
                displayValue: targetMetric.entityTargetCondition?.entityIds.map((entityId) => {
                    return getEntityDisplayName(props.projectId, entityId, appDataState, appDataDispatch);
                }).join(', ') ?? '',
                editable: true,
            } : undefined,
            exclude: targetMetric.entityTargetCondition ? {
                label       : capitalizeAll(trans('exclude')),
                displayValue: targetMetric.entityTargetCondition?.exclude ? 'true' : 'false',
                editable    : true,
            } : undefined,
        };

        return fields;
    }, [entityClasses, props.projectId, trans, appDataDispatch, appDataState]);



    /**
     * Helper function to create the fields that display the attributes for condition threshold
     */
    const getConditionThresholdFields = useCallback((threshold: MetricsAlertConditionThreshold): ThresholdFields => {
        return {
            severity: {
                label       : capitalizeAll(trans('alerting.label.severity')),
                value       : threshold.severity,
                displayValue: capitalizeAll(trans(getAlertSeverityDisplayName(threshold.severity))),
                editable    : true,
            },
            operator: {
                label       : capitalizeAll(trans('operator')),
                displayValue: threshold.operator,
                editable    : true,
            },
            operands: {
                label       : capitalizeAll(trans('operands')),
                displayValue: threshold.operands.join(', '),
                editable    : true,
            },
        };
    }, [trans]);


    /**
     * Helper function to create the fields that display the attributes for condition
     */
    const getConditionFields = useCallback((alertRule: AlertRule): AlertRuleInfoProps['fields']['condition'] => {
        if (isMetricsHeartbeatAlertCondition(alertRule.condition)) {
            return {
                severity: {
                    label       : capitalizeAll(trans('alerting.label.severity')),
                    value       : alertRule.condition.severity,
                    displayValue: capitalizeAll(trans(getAlertSeverityDisplayName(alertRule.condition.severity))),
                    editable    : true,
                },
                targetMetric: getTargetMetricFields(alertRule.condition.metricsCondition),
            };
        }
        if (isMetricsThresholdAlertCondition(alertRule.condition)) {
            return {
                aggregation: {
                    label       : capitalizeAll(trans('alerting.label.aggregation')),
                    displayValue: capitalizeAll(trans(getTimeSeriesAggregationTypeName(alertRule.condition.aggregation))),
                },
                targetMetric: getTargetMetricFields(alertRule.condition.metricsCondition),
                thresholds  : alertRule.condition.thresholds.map((threshold) => {
                    return getConditionThresholdFields(threshold);
                }),
            };
        }
        return undefined;
    }, [getConditionThresholdFields, getTargetMetricFields, trans]);



    /**
     * Set up ALL the fields for the Alert Rule info component and cache it. These are the fields for showing the alert rule's attributes
     * e.g. id, name, description, targets, conditions, etc.... This will be passed to the AlertRuleInfo component later.
     */
    const alertRuleCompFields = useMemo((): AlertRuleInfoProps['fields'] | undefined => {

        if (selectedAlertRule) {

            return {
                problem: selectedAlertRule.problem ? {
                    label       : capitalizeAll(trans('alerting.label.problem')),
                    displayValue: capitalizeAll(trans(getErrorMessage(selectedAlertRule.problem))),
                } : undefined,
                enabled: {
                    label       : capitalizeAll(trans('label.status')),
                    displayValue: capitalizeAll(trans(selectedAlertRule.enabled ? 'label.enabled' : 'label.disabled')),
                    value       : selectedAlertRule.enabled,
                },
                id: {
                    label       : capitalizeAll(trans('label.id')),
                    displayValue: selectedAlertRule.id.toString(),
                },
                type: {
                    label       : capitalizeAll(trans('alerting.alert_condition_type')),
                    displayValue: capitalizeAll(trans(
                        getAlertConditionTypeDisplayName(selectedAlertRule.condition.conditionType) ?? 'label.unknown',
                    )),
                },
                name: {
                    label       : capitalizeAll(trans('label.name')),
                    editable    : true,
                    displayValue: selectedAlertRule.name,
                },
                description: {
                    label       : capitalizeAll(trans('label.description')),
                    editable    : true,
                    displayValue: selectedAlertRule.description,
                },
                updatedTime: {
                    label       : capitalizeAll(trans('label.date_updated')),
                    displayValue: getDateTimeAndElapsedTime(selectedAlertRule.updateTime, trans, false),
                },
                creationTime: {
                    label       : capitalizeAll(trans('label.date_created')),
                    displayValue: getDateTimeAndElapsedTime(selectedAlertRule.creationTime, trans, false),
                },
                condition      : getConditionFields(selectedAlertRule),
                messageTemplate: {
                    title: {
                        label   : capitalizeAll(trans('alerting.message_template.title')),
                        value   : selectedAlertRule.messageTemplate?.title,
                        editable: true,
                    },
                    recoveryTitle: {
                        label   : capitalizeAll(trans('alerting.message_template.recovery_title')),
                        value   : selectedAlertRule.messageTemplate?.recoveryTitle,
                        editable: true,
                    },
                    message: {
                        label   : capitalizeAll(trans('alerting.message_template.message')),
                        value   : selectedAlertRule.messageTemplate?.message,
                        editable: true,
                    },
                    recoveryMessage: {
                        label   : capitalizeAll(trans('alerting.message_template.recovery_message')),
                        value   : selectedAlertRule.messageTemplate?.recoveryMessage,
                        editable: true,
                    },
                },
                actions: selectedAlertRule.actions?.map((action) => {
                    return {
                        recipients: {
                            label       : capitalizeAll(trans('label.recipient', { count: 2 })),
                            // Combine all the recipients into 1 string instead of creating individual input field
                            displayValue: action.recipients.map((recipient) => {
                                return getUserFriendlyRecipientDisplay(recipient, trans);
                            }).join('\n'),
                            editable: true,
                        },
                    };
                }),
            };
        }

        return undefined;
    }, [getConditionFields, selectedAlertRule, trans]);



    /**
     * Set up the texts for the Alert Rule info component and cache it. Will pass this to the AlertRuleInfo component later
     */
    const alertRuleCompTexts = useMemo((): AlertRuleInfoProps['texts'] => {
        return {
            alertConditionsTitle: capitalizeAll(trans('alerting.label.condition')),
            metricTargetTitle   : capitalizeAll(trans('alerting.label.target')),
            metricTargetsTitle  : capitalizeAll(trans('alerting.label.target_other')),
            messageTemplateTitle: capitalizeAll(trans('alerting.label.message_template')),
            actionsTitle        : capitalizeAll(trans('alerting.label.actions')),
        };
    }, [trans]);



    /**
     * Set up the actions for the Alert Rule info component. The only action that this component need is the 'startEdit' action which
     * must be handled by the parent container.
     */
    const alertRuleCompActions = useMemo((): AlertRuleInfoProps['actions'] => {
        if (!props.canEditAlertRule) {
            return undefined;
        }

        return {
            startEdit: {
                onClick: () => {
                    props.onEditAlertRule();
                },
            },
        };
    }, [props]);


    return (
        <>
            {alertRuleCompFields && (
                <AlertRuleInfo
                    className={props.className}
                    style={props.style}
                    title={capitalizeAll(trans('alerting.alert_rule.into_panel.title'))}
                    icon="notifications"
                    fields={alertRuleCompFields}
                    texts={alertRuleCompTexts}
                    actions={alertRuleCompActions}
                    onFieldClicked={props.onEditAlertRule}
                />
            )}

            {!alertRuleCompFields && (
                <CircularProgress />
            )}
        </>
    );
};
